import { CollegeProductionRow } from '../types/CollegeProductionData';
import Player from '../types/Player';
import { CONFERENCE, CONFERENCE_NAME } from '../templates/TeamPageTemplate';
import { DefensivePosition, OffensivePosition } from '../types/Position';
export type ProfilerType = 'qb' | 'rusher' | 'receiver' | 'defender';

export interface ProfilerLink {
  type: ProfilerType;
  label: string;
  url: string;
  year: number;
  teamName?: string;
}

export interface PlayerProfilerLinks {
  primary?: ProfilerLink;
  additional: ProfilerLink[];
  all: ProfilerLink[];
}

export interface ProfilerConfig {
  fbsProfilerMinYear: number;
  fcsProfilerMinYear: number;
  qbProfilerMinPassingAttempt: number;
  rusherProfilerMinRushingAttempt: number;
  receiverProfilerMinReceptions: number;
  defenderProfilerMinDefensiveSnaps: number;
}

const SSA_PROFILER_BASE_URL = 'https://ssa.trackingfootball.com';
const FBS_PROFILER_MIN_YEAR = 2017;
const FCS_PROFILER_MIN_YEAR = 2021;
const QB_PROFILER_MIN_PASSING_ATTEMPT = 20;
const RUSHER_PROFILER_MIN_RUSHING_ATTEMPT = 20;
const RECEIVER_PROFILER_MIN_RECEPTIONS = 10;
const DEFENDER_PROFILER_MIN_DEFENSIVE_SNAPS = 100;

const DEFAULT_PROFILER_CONFIG: ProfilerConfig = {
  fbsProfilerMinYear: FBS_PROFILER_MIN_YEAR,
  fcsProfilerMinYear: FCS_PROFILER_MIN_YEAR,
  qbProfilerMinPassingAttempt: QB_PROFILER_MIN_PASSING_ATTEMPT,
  rusherProfilerMinRushingAttempt: RUSHER_PROFILER_MIN_RUSHING_ATTEMPT,
  receiverProfilerMinReceptions: RECEIVER_PROFILER_MIN_RECEPTIONS,
  defenderProfilerMinDefensiveSnaps: DEFENDER_PROFILER_MIN_DEFENSIVE_SNAPS,
};

const DEFENDER_POSITION_CODES = new Set<string>(Object.values(DefensivePosition));
const QB_POSITION_CODES = new Set<string>([OffensivePosition.QB]);
const RECEIVER_POSITION_CODES = new Set<string>([OffensivePosition.WR, OffensivePosition.TE]);
const RUSHER_POSITION_CODES = new Set<string>([OffensivePosition.RB, OffensivePosition.FB]);

function toNumber(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizePositionCode(position?: string): string {
  if (!position) return '';
  return String(position).trim().toUpperCase();
}

function inferSubdivisionFromRow(row: CollegeProductionRow): string {
//   const raw = String((row as any).conferenceMainLevel || '').trim().toLowerCase();
  const raw = String((row as any).conferenceMainLevel || '').trim().toLowerCase();
  if (raw === CONFERENCE.DIV_I_AA.toLowerCase()) {
    return CONFERENCE_NAME[CONFERENCE.DIV_I_AA];
  }

  if (raw === CONFERENCE.DIV_I_A.toLowerCase()) {
    return CONFERENCE_NAME[CONFERENCE.DIV_I_A];
  }

  return CONFERENCE_NAME[CONFERENCE.DIV_I_A];

}

function isSupportedYear(row: CollegeProductionRow, config: ProfilerConfig): boolean {
  const year = toNumber(row.year);
  const subdivision = inferSubdivisionFromRow(row);
  const minYear = subdivision === CONFERENCE_NAME[CONFERENCE.DIV_I_AA] ? config.fcsProfilerMinYear : config.fbsProfilerMinYear;
  return year >= minYear;
}

function findMostRecentQualifiedRow(
  rows: CollegeProductionRow[],
  config: ProfilerConfig,
  qualifier: (row: CollegeProductionRow) => boolean,
): CollegeProductionRow | undefined {
  const sorted = [...rows].sort((a, b) => toNumber(b.year) - toNumber(a.year));
  return sorted.find((row) => isSupportedYear(row, config) && qualifier(row));
}

function buildQbProfilerUrl(pffId: string, year: number): string {
  const query = new URLSearchParams({
    mode: 'CFB',
    qbYear: String(year),
    qb: pffId,
    stats: 'All Passing Plays',
  });
  return `${SSA_PROFILER_BASE_URL}/qb-profiler-pff/?${query.toString()}`;
}

function buildRusherProfilerUrl(pffId: string, year: number, teamName: string): string {
  const query = new URLSearchParams({
    mode: 'CFB',
    rushYear: String(year),
    team: teamName,
    rusher: pffId,
    position: '',
    stats: 'All Rushes',
  });
  return `${SSA_PROFILER_BASE_URL}/rusher-profile-pff/?${query.toString()}`;
}

function buildReceiverProfilerUrl(pffId: string, year: number, teamName: string): string {
  const query = new URLSearchParams({
    mode: 'CFB',
    recYear: String(year),
    team: teamName,
    rec: pffId,
    position: '',
    stats: 'All Targets',
  });
  return `${SSA_PROFILER_BASE_URL}/receiver-profile-pff/?${query.toString()}`;
}

function buildDefenderProfilerUrl(pffId: string, year: number, teamName: string): string {
  const query = new URLSearchParams({
    mode: 'CFB',
    defYear: String(year),
    team: teamName,
    def: pffId,
    role: 'pr',
    position: '',
    stats: 'All Pass Rush',
  });
  return `${SSA_PROFILER_BASE_URL}/defender-profile-pff/?${query.toString()}`;
}

function resolvePrimaryPositionCode(player?: Player): string {
  const primaryCollege = (player?.playerColleges || []).find((college) => Boolean(college?.isPrimary));
  const primaryCollegePosition = primaryCollege?.positions?.find((pos) => pos.isPrimary)?.code;
  const preferred = primaryCollegePosition || player?.collegePosition || player?.hsPosition || '';
  return normalizePositionCode(String(preferred).split('/')[0]);
}

function preferredProfilerTypeForPosition(positionCode: string): ProfilerType | undefined {
  if (QB_POSITION_CODES.has(positionCode)) {
    return 'qb';
  }

  if (RECEIVER_POSITION_CODES.has(positionCode)) {
    return 'receiver';
  }

  if (RUSHER_POSITION_CODES.has(positionCode)) {
    return 'rusher';
  }

  if (DEFENDER_POSITION_CODES.has(positionCode)) {
    return 'defender';
  }

  return undefined;
}

export function buildPlayerProfilerLinks(
  player?: Player,
  rows: CollegeProductionRow[] = [],
  configOverrides?: Partial<ProfilerConfig>,
): PlayerProfilerLinks {
  const pffId = rows?.[0]?.pffId ? String(rows[0].pffId).trim() : '';
  const config: ProfilerConfig = {
    ...DEFAULT_PROFILER_CONFIG,
    ...configOverrides,
  };
  if (!pffId || rows.length === 0) {
    return { primary: undefined, additional: [], all: [] };
  }

  const all: ProfilerLink[] = [];

  const qbRow = findMostRecentQualifiedRow(rows, config, (row) => toNumber(row.passingAtt) >= config.qbProfilerMinPassingAttempt);
  if (qbRow) {
    all.push({
      type: 'qb',
      label: 'QB Profiler',
      year: toNumber(qbRow.year),
      url: buildQbProfilerUrl(pffId, toNumber(qbRow.year)),
    });
  }

  const rusherRow = findMostRecentQualifiedRow(rows, config, (row) => toNumber(row.rushingAtt) >= config.rusherProfilerMinRushingAttempt);
  if (rusherRow && rusherRow.teamName) {
    all.push({
      type: 'rusher',
      label: 'Rusher Profiler',
      year: toNumber(rusherRow.year),
      teamName: rusherRow.teamName,
      url: buildRusherProfilerUrl(pffId, toNumber(rusherRow.year), rusherRow.teamName),
    });
  }

  const receiverRow = findMostRecentQualifiedRow(rows, config, (row) => toNumber(row.receiving) >= config.receiverProfilerMinReceptions);
  if (receiverRow && receiverRow.teamName) {
    all.push({
      type: 'receiver',
      label: 'Receiver Profiler',
      year: toNumber(receiverRow.year),
      teamName: receiverRow.teamName,
      url: buildReceiverProfilerUrl(pffId, toNumber(receiverRow.year), receiverRow.teamName),
    });
  }

  const defenderRow = findMostRecentQualifiedRow(rows, config, (row) => toNumber(row.snaps) >= config.defenderProfilerMinDefensiveSnaps);
  if (defenderRow && defenderRow.teamName) {
    all.push({
      type: 'defender',
      label: 'Defender Profiler',
      year: toNumber(defenderRow.year),
      teamName: defenderRow.teamName,
      url: buildDefenderProfilerUrl(pffId, toNumber(defenderRow.year), defenderRow.teamName),
    });
  }

  if (!all.length) {
    return { primary: undefined, additional: [], all };
  }

  const preferredType = preferredProfilerTypeForPosition(resolvePrimaryPositionCode(player));
  const primary = all.find((item) => item.type === preferredType) || all[0];
  const additional = all.filter((item) => item.type !== primary.type);

  return { primary, additional, all };
}
